import {PartyRole, PayoutInstructionStatus} from '@sollapay/enums';
import {
  Divider,
  ListFilterCheckboxSection,
  ListFilterCreatedAtSection,
  ListFilterHeader
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {
  CREATED_AT_FILTER_VALUES,
  DEFAULT_PAYOUTS_FILTER_STATE
} from './config/payouts-filter.config';
import {isPayoutsFilterActive} from './utils/payouts-filter.utils';

import type {PayoutsFilterState} from './config/payouts-filter.config';
import type {CreatedAtFilter} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface PayoutsFilterMenuProps {
  filter: PayoutsFilterState;
  onChange: (filter: PayoutsFilterState) => void;
}

const CREATED_AT_KEYS: Record<CreatedAtFilter, string> = {
  all: 'filter.createdAt.all',
  this_week: 'filter.createdAt.thisWeek',
  '7d': 'filter.createdAt.last7days',
  '30d': 'filter.createdAt.last30days',
  '90d': 'filter.createdAt.last90days'
};

const STATUS_OPTIONS: PayoutInstructionStatus[] = [
  PayoutInstructionStatus.SENT,
  PayoutInstructionStatus.CLARIFICATION_REQUESTED,
  PayoutInstructionStatus.APPROVED,
  PayoutInstructionStatus.FUNDS_IN_TRANSIT,
  PayoutInstructionStatus.COMPLETED,
  PayoutInstructionStatus.REJECTED,
  PayoutInstructionStatus.CANCELLED,
  PayoutInstructionStatus.FAILED
];

const ROLE_OPTIONS: PartyRole[] = Object.values(PartyRole);

export const PayoutsFilterMenu: FC<PayoutsFilterMenuProps> = ({filter, onChange}) => {
  const {t} = useTranslation('trustAccounts');

  const createdAtOptions = CREATED_AT_FILTER_VALUES.map(value => ({
    value,
    label: t(CREATED_AT_KEYS[value])
  }));

  const statusOptions = STATUS_OPTIONS.map(status => ({
    value: status,
    label: t(`view.payouts.list.status.${status}`)
  }));

  const roleOptions = ROLE_OPTIONS.map(role => ({
    value: role,
    label: t(`partyRole.${role}`)
  }));

  return (
    <div data-slot="payouts-filter-menu" className="flex flex-col py-2">
      <ListFilterHeader
        title={t('view.payouts.list.filter.title')}
        clearLabel={t('view.payouts.list.filter.clearAll')}
        isActive={isPayoutsFilterActive(filter)}
        onClearAll={() => onChange(DEFAULT_PAYOUTS_FILTER_STATE)}
      />
      <ListFilterCreatedAtSection
        sectionLabel={t('filter.sections.createdAt')}
        options={createdAtOptions}
        createdAt={filter.datePreset}
        hasDateRange={false}
        onChange={datePreset => onChange({...filter, datePreset})}
      />
      <Divider className="my-1" />
      <ListFilterCheckboxSection
        title={t('view.payouts.list.filter.sections.status')}
        options={statusOptions}
        selected={filter.statuses}
        onChange={vals => onChange({...filter, statuses: vals as PayoutInstructionStatus[]})}
      />
      <Divider className="my-1" />
      <ListFilterCheckboxSection
        title={t('view.payouts.list.filter.sections.role')}
        options={roleOptions}
        selected={filter.roles}
        onChange={vals => onChange({...filter, roles: vals as PartyRole[]})}
      />
      <Divider className="my-1" />
      <div className="px-3 pb-1 pt-2">
        <span className="text-xs font-semibold text-fg-tertiary">
          {t('view.payouts.list.filter.sections.amountRange')}
        </span>
      </div>
      <div className="flex items-start gap-2 px-3 pb-2 pt-1">
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-xs font-medium text-fg-secondary">
            {t('view.payouts.list.filter.amountRange.from')}
          </span>
          <div className="relative">
            <span
              data-slot="icon"
              className="pointer-events-none absolute inset-e-2 top-1/2 -translate-y-1/2 text-xs text-fg-tertiary"
            >
              ₪
            </span>
            <input
              type="number"
              min={0}
              value={filter.amountMin ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                onChange({...filter, amountMin: val});
              }}
              className="input-base h-9 w-full pe-6 text-sm"
              placeholder="0"
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="text-xs font-medium text-fg-secondary">
            {t('view.payouts.list.filter.amountRange.to')}
          </span>
          <div className="relative">
            <span
              data-slot="icon"
              className="pointer-events-none absolute inset-e-2 top-1/2 -translate-y-1/2 text-xs text-fg-tertiary"
            >
              ₪
            </span>
            <input
              type="number"
              min={0}
              value={filter.amountMax ?? ''}
              onChange={e => {
                const val = e.target.value === '' ? undefined : Number(e.target.value);
                onChange({...filter, amountMax: val});
              }}
              className="input-base h-9 w-full pe-6 text-sm"
              placeholder="∞"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
